// API definition is available at http://deployment.sureviewsystems.com/api/swagger/v1/swagger.json

import {
	axiosInstance,
	axiosJsonDirectInstance,
	axiosJsonExternalInstance,
	axiosJsonInstance,
	axiosNoContentTypeInstance
} from "@/axios.instance";
import {
	AlarmQueueFilterID,
	CallWaitingPref,
	HandleEventResult,
	HighPriorityAcceptRequest,
	HighPriorityIgnoreRequest,
	PickupCall
} from "@/store/eventqueue/types";
import {
	ContactUser,
	Domain,
	DomainGroup,
	PasswordChangeResult,
	RecoveryQuestion,
	RenewPasswordDetails,
	ResetPasswordDetails,
	ResetPasswordResponse,
	UserDetails,
	UserQuestionsUpdate
} from "@/store/user-management/types";
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, CancelToken, CancelTokenSource } from "axios";
import { AuditRecordDTO, EventDetails, EventRecordDetails, MaskReason } from "@/store/site-monitor/types";
import { Group, ServerTypeEvent, Tag } from "@/types";
import { AlarmAction, AlarmActionType, Response, ResponseTestResult } from "@/store/responses/types";
import { ExternalMapLayer } from "@/store/external-map-layers/types";
import { ReportTitles } from "@/store/reports/types";
import { Pref, PrefTypeRequest } from "@/store/settings/types";
import {
	DeviceDetails,
	DeviceSearchResult,
	DeviceTypeCounts,
	GetDevicesByTypeParams,
	ServerDetails,
	ServerLineProfile,
	ServersWithEnabledDeviceTypesParams,
	ServerType,
	DeviceConfigurationPreset,
	DeviceConfigurationPresetPayload
} from "@/store/devices/types";
import { EventCategory } from "@/store/event-category-setup/types";
import { CaseData } from "@/store/case-management/types";
import {
	ActionPayload,
	ActionPlan,
	ActionPlanCreate,
	ActionTrigger,
	QueryNotificationServers,
	TaskCategory,
	TaskLibrary
} from "@/store/tasks/types";
import { CameraPreviewRequest, CameraPreviewResponse, ManualResetRequest } from "@/store/manual-actions/types";
import {
	LoginPreferences,
	LoginResult,
	OktaRequestResponse,
	OktaRequestSuccessfulResponse,
	PaginatedSearchQueryParams,
	Region,
	RegionLoginResult,
	ResponseSearchRequest,
	SingleEventLoginResult,
	UlType,
	User
} from "@/store/types";
import CloseEventDTO from "@/types/CloseEventDTO";
import CloseMultipleEventsDTO from "@/types/CloseMultipleEventsDTO";
import UpdateMultipleGuardsDTO from "@/types/CloseMultipleEventsDTO";
import CloseEventResultDTO from "@/types/CloseEventResultDTO";
import EventOutcomeDTO, { EventOutcomeResponse } from "@/types/EventOutcomeDTO";
import { AddMultipleGuardsDTO, Guard } from "@/store/dispatch/types";
import { PasswordComplexityRequest, ValidateSitePasswordRequest } from "@/store/my-account/types";
import { MaskAreaRequest, UnmaskAreaRequest } from "@/store/arm-disarm-area/types";
import { AudioDevice } from "@/types/audio/audio";
import { FeatureTreeNode } from "@/store/views/types";
import {
	MapItemData,
	MapItemLocationIdentifierOption,
	MapLayer,
	MapLayerBounds,
	MapLayerData,
	MapRequest
} from "@/store/map-layers/types";
import qs from "qs";
import { ExternalMenuLink } from "@/store/external-menu-link/types";
import ResponseAlarmType from "@/types/responses/ResponseAlarmType";
import QuickControlResponse from "@/types/QuickControlResponse";
import { GetDevicesByExtraValuesPayload } from "@/store/lockdown/types";
import { TaskCategoryType } from "@/models/Tasks/TaskCategoryType";
import { BulkImportType } from "@/bulk-import/BulkImport";
import { BulkImportResponse } from "@/types/BulkImportResponse";
import { EventSearchPrefs } from "@/store/event-search/types";
import { AlarmPointHistory } from "@/types/AlarmPoints";
import { ServerTypeSetupPrefs } from "@/types/serverTypeEvent";
import { EventOutcome } from "@/types/reports/Filters";
import { identifyAndEncode } from "@/bulk-import/CommonBulkImport";

// @todo refactor to models
export interface UserCredentials {
	username: string;
	password: string;
	IdToken?: string | null | undefined;
}

interface ScreenshotInfo {
	DeviceID: number;
	Image: string;
}
interface InterDataServiceLoginTokenContainer {
	Key: string;
	RegionalUserID: number;
}
interface UserLoginData {
	sessionID: string;
	accessCode: string;
}

export interface CompleteTaskPayload {
	EventID: number;
	EventTaskID: string;
	TaskComplete: boolean;
	Result?: string;
	taskData?: any;
}

export interface CallContactRequest {
	GroupId: number;
	All: boolean;
	Parent: boolean;
	CallType: string;
	EventTaskId: null | number;
}

// @contributing
// awoid naming methods prefixed `get`, e.g. no getEvents,
// using `load` hints at presence of an async operation

class ImmixCloudV2ApiHttp {
	public static readonly apiRoot = "";
	// true for formdata payloads, false for application/json, applies to POST and PUT
	private readonly STRINGIFY_PAYLOADS: boolean = true;

	private axios: AxiosInstance;
	private axiosJson: AxiosInstance;
	private axiosPost: AxiosInstance;
	private axiosJsonDirectInstance: AxiosInstance;
	private axiosJsonExternalInstance: AxiosInstance;
	private axiosNoContentTypeInstance: AxiosInstance;

	constructor() {
		this.axios = axiosInstance;
		this.axiosJson = axiosJsonInstance;

		// Used to skip the interceptor if we want to handle our exceptions
		// in the component/action using the api.
		this.axiosJsonDirectInstance = axiosJsonDirectInstance;
		this.axiosJsonExternalInstance = axiosJsonExternalInstance;
		this.axiosNoContentTypeInstance = axiosNoContentTypeInstance;
	}

	public async retrieveServerName(): Promise<string> {
		return (await axiosInstance.get("/Values/ServerName")).data;
	}

	public async applicationInformation(): Promise<any> {
		const versionRequest = axios.create({
			headers: {
				Accept: "application/json, text/javascript, */*; q=0.01",
				"Content-Type": "application/json"
			}
		});

		try {
			const response = await versionRequest.get("./manifest.json");
			return response.data;
		} catch (err) {
			return {};
		}
	}

	public get getOriginalApiUrl() {
		return axiosInstance.prototype._originalBaseUrl;
	}

	public setBaseApiUrl(apiUrl: string) {
		this.axios.prototype.setApiBaseUrl(apiUrl);
	}

	public resetBaseApiUrl() {
		this.axios.prototype.resetBackToOriginalBaseUrl();
	}

	public async saveCameraScreenshot(data: ScreenshotInfo) {
		this.axios.post("/Devices/SaveCameraSnapshot", data);
	}

	public async login(credentials: UserCredentials): Promise<AxiosResponse<LoginResult>> {
		const response = await this.axios.post("/login", credentials);
		return response;
	}

	public async logout(): Promise<any> {
		const response = await this.axios.get("/Login/Logout");
		return response;
	}

	public async checkLogin(): Promise<LoginResult> {
		const { data } = await this.axiosJsonDirectInstance.get("/Login/CheckLogin");
		return data;
	}

	public async singleEventLogin(credentials: UserLoginData): Promise<AxiosResponse<SingleEventLoginResult>> {
		const response = await this.axios.post("/login/SingleEvent", credentials);
		return response;
	}

	public async loginToRegion(
		token: InterDataServiceLoginTokenContainer,
		regionUrl: string
	): Promise<AxiosResponse<RegionLoginResult>> {
		const response = await this.axios.post("/login/InterDataServiceLogin", token, {
			baseURL: regionUrl
		});

		return response;
	}

	public async logoutRegion(regionUrl: string): Promise<AxiosResponse<any>> {
		const response = await this.axios.get("/Login/Logout", {
			baseURL: regionUrl
		});

		return response;
	}

	public async getInterDataServiceLoginToken(regionId: number): Promise<any> {
		return (await this.axios.get(`/Login/GenerateInterDataServiceLoginToken/${regionId}`)).data;
	}

	// -- Okta SSO

	public async addOktaRequest(serverAddress: string): Promise<AxiosResponse<OktaRequestResponse>> {
		return await this.axios.post("/login/addOktaRequest", { serverAddress });
	}

	public async addErrorToSystemLog(eventId, title, errorMessage): Promise<any> {
		return await this.axiosJson.post("/SystemLog", {
			eventId,
			title,
			errorMessage
		});
	}

	public async loadMapLayers(
		mapQuery: MapRequest,
		cancelToken: CancelTokenSource = null
	): Promise<AxiosResponse<MapLayer>> {
		const response = await this.axios.post("/MapLayers", mapQuery, {
			cancelToken: cancelToken?.token
		});
		return response;
	}

	public async loadMapLayersItemTypes(): Promise<any> {
		const response = await this.axios.get("/MapLayers/ItemTypes");
		return response;
	}

	public async loadMapLayersBounds(): Promise<any> {
		const response = await this.axios.get("/MapLayers/Bounds");
		return response;
	}

	public async loadAreaMapLayersBounds(
		areaId: number,
		regionId: number | null = null
	): Promise<AxiosResponse<MapLayerBounds>> {
		const response = await this.axios.get(`/MapLayers/Bounds/${areaId}`, {
			params: {
				regionId
			}
		});
		return response;
	}

	public async searchAssets(mapQuery: any): Promise<any> {
		const response = await this.axios.get("/MapLayers/SearchAssets", {
			params: mapQuery
		});
		return response.data;
	}

	public async searchAssetsByDistance(mapQuery: any): Promise<AxiosResponse<any>> {
		const response = await this.axios.get("/MapLayers/SearchAssetsByDistance", {
			params: mapQuery,
			paramsSerializer: params => {
				return qs.stringify(params);
			}
		});
		return response.data;
	}

	public async closeEvent(data: CloseEventDTO): Promise<CloseEventResultDTO> {
		const response = await this.axios.post("/Event/Close", data);
		return response.data;
	}

	public async forceCloseMultipleEvents(data: CloseMultipleEventsDTO): Promise<CloseEventResultDTO> {
		const response = await this.axios.post("/Event/ForceCloseMultiple", data);
		return response.data;
	}

	public async flagEventForReview(eventId: number, flagged: boolean): Promise<boolean> {
		const { data } = await this.axios.post("/Event/Flag", {
			eventId,
			flagged
		});

		return data;
	}

	public async preserveEvent(eventId: number, preserved: boolean): Promise<boolean> {
		const { data } = await this.axios.post("/Event/Preserve", {
			eventId,
			preserved
		});

		return data;
	}

	public async groupsForFilter(filterId: any): Promise<any[]> {
		return (
			await this.axios.get("/AlarmQueueFilters/GroupsForFilter", {
				params: {
					AlarmQueueFilterID: filterId
				}
			})
		).data;
	}
	public async loadAlarmQueueFiltersForGuards(): Promise<any[]> {
		const { data } = await this.axios.get("/DispatchSetup/AlarmFilters");
		var mappedFilters = data.map(item => {
			const container = { title: "", alarmQueueFilterID: null };
			container.title = item.title;
			container.alarmQueueFilterID = item.alarmQueueFilterID;

			return container;
		});
		return mappedFilters;
	}
	public async loadEventQueueFiltered(filters: any): Promise<any> {
		const response = await this.axios.post("/eventqueue", filters);
		return response;
	}

	public async eventHandleTop(filter: AlarmQueueFilterID): Promise<any> {
		const regionId = filter.regionId > 0 ? filter.regionId : null;
		const { data } = await this.axios.get(`/EventQueue/HandleTop/${filter.filterId}`, {
			params: { regionId }
		});

		return data;
	}

	public async loadEventQueueFull(): Promise<any> {
		const response = await this.axios.get("/eventqueue");
		return response;
	}

	public async postAudit(data: AuditRecordDTO): Promise<any> {
		// If we have a regionURL, use it. Otherwise pass no config.
		const response = await this.axios.post("/EventRecords", data, { regionUrl: data.regionUrl });
		return response;
	}

	public async auditReviewAction(data: any) {
		const response = await this.axios.post("/EventRecords/AuditReview", data);
		return response;
	}

	public async forceRestore({ eventRecordID, restoralReason }): Promise<any> {
		return await this.axios.post("/EventRecords/ForceRestore", {
			EventRecordID: eventRecordID,
			Reason: restoralReason
		});
	}

	public devicesByGroup(groupID: number, params: any): Promise<any> {
		return axiosInstance.get("/devices/byGroup/" + groupID, {
			params
		});
	}

	public async allDevicesByGroup(groupID: number, params: any) {
		params.page = 1;
		var loadMore = true;
		var allDevices = [];

		while (loadMore) {
			var sample = await axiosInstance.get("/devices/byGroup/" + groupID, {
				params
			});

			sample.data.forEach(record => {
				allDevices.push(record);
			});

			if (sample.data.length < 100) {
				loadMore = false;
			}

			params.page = params.page + 1;
		}

		return allDevices;
	}

	public async devicesSearchNear(params: any) {
		return await axiosInstance.get("/Devices/SearchNear", {
			params
		});
	}

	public async deviceAction(deviceID: number, payload: any): Promise<any> {
		return axiosInstance.post("/devices/" + deviceID, payload);
	}

	public async loadDeviceById(deviceId: number, params: any, regionUrl: string = null): Promise<any> {
		return this.axiosJsonDirectInstance.get(`/devices/${deviceId}`, { params, regionUrl });
	}

	public async getByServerId(serverId: number): Promise<any> {
		const { data } = await this.axios.get(`/devices/GetByServerId?serverId=${serverId}`);
		return data;
	}

	public async deviceQuickControl(
		deviceID: number,
		Reason: string,
		eventId: number,
		regionUrl: string = null
	): Promise<AxiosResponse<QuickControlResponse>> {
		const payload = {
			deviceID,
			Reason,
			EventId: eventId
		};

		return await this.axios.post("/devices/QuickControl", payload, { regionUrl });
	}

	public async quickControlPreviewForDevice(
		payload: CameraPreviewRequest,
		regionUrl: string = undefined
	): Promise<AxiosResponse<CameraPreviewResponse>> {
		return await this.axios.post("/QuickControl/CameraPreviewForDevice", payload, { regionUrl });
	}

	public async quickControlPreviewForResponse(
		payload: CameraPreviewRequest,
		regionUrl: string = undefined
	): Promise<AxiosResponse<CameraPreviewResponse>> {
		return await this.axios.post("/QuickControl/CameraPreviewForResponse", payload, { regionUrl });
	}

	public async deviceSearch(
		deviceTypeId: number,
		query: string,
		cancelToken: any,
		searchTermsInAnyOrder: boolean = false,
		regionUrl: string = undefined
	): Promise<AxiosResponse<DeviceSearchResult[]>> {
		const config: AxiosRequestConfig = {
			params: {
				search: query,
				searchTermsInAnyOrder: searchTermsInAnyOrder
			},
			cancelToken,
			regionUrl
		};

		return this.axios.get(`/devices/search/${deviceTypeId}`, config);
	}

	public async deviceSearchNear(params): Promise<any> {
		return this.axios.get("/Devices/SearchNear", {
			params
		});
	}

	public async deviceSearchAll(query: string, cancelToken: any): Promise<any> {
		return this.axios.get("/Devices/SearchAll", {
			params: {
				search: query
			},
			cancelToken
		});
	}

	public async unlinkCamera({ objectId, deviceId }): Promise<any> {
		return await this.axios.get(`/Responses/Unlink/${objectId}/${deviceId}`);
	}

	public async createResponseActionForEventRecord({ eventRecordId, title, priority, groupId }): Promise<any> {
		return await this.axios.post(`/Responses/Link/${eventRecordId}/${title}/${priority}/${groupId}`);
	}

	public async EventPark(payload): Promise<any> {
		return this.axios.post("/Event/Park", payload);
	}

	public async raiseManualAlarm(payload): Promise<any> {
		return await this.axiosJson.post("/Event", payload);
	}

	public async eventControl(eventObject): Promise<any> {
		// eventObject is the payload.
		// Post using axiosJsonInstance, with path/route and payload.
		return await this.axiosJson.post("/Event/Patrol", eventObject);
		// ^^ return data.
	}

	public async tourNotesRequired(): Promise<any> {
		// Calls Backend to see if the notes are required or not.
		return await this.axios.get(`/Event/Patrol/NoteRequired`);
	}

	public async disableAutomaticLoad(): Promise<any> {
		return await this.axios.get(`/Event/AlarmPoint/DisableAutomaticLoad`);
	}

	public async EventPassControl(eventid: number | string, userId: number): Promise<any> {
		return this.axios.get("/EventShare/PassControl/" + eventid, {
			params: {
				userId
			}
		});
	}

	public async leaveEvent(EventID: number): Promise<any> {
		return await this.axios.post(`/EventShare/LeaveEvent?eventId=${EventID}`);
	}

	public async changeEventRoutingGroup(data: any): Promise<any> {
		const response = await this.axios.post("/RoutingGroups/Raise", data);
		return response;
	}

	public async loadEventHandleAllowInProcessing(
		eventId: number,
		allowInProcessing?: any,
		regionUrl?: string
	): Promise<AxiosResponse<HandleEventResult>> {
		const response = await this.axios.get(`EventQueue/Handle/${eventId}?AllowInProcessing=${allowInProcessing}`, {
			baseURL: regionUrl || this.getOriginalApiUrl
		});
		return response;
	}

	public async clearEventQueue(clearEventID: number): Promise<any> {
		const response = await this.axios.get(`EventQueue/Clear/${clearEventID}`);
		return response;
	}

	public async loadResponseStates(): Promise<any> {
		const { data } = await this.axios.get("eventqueue/ResponseState");
		return data;
	}

	public async loadAlarmQueueFilters(): Promise<any> {
		return await this.axios.get("/AlarmQueueFilters");
	}

	public async addAlarmQueueFilter(alarmQueueFilterID: AlarmQueueFilterID): Promise<any> {
		const endpoint = `/AlarmQueueFilters/AddFilter/${alarmQueueFilterID.filterId}`;
		const regionId = alarmQueueFilterID.regionId > 0 ? alarmQueueFilterID.regionId : null;

		return await this.axios.get(endpoint, {
			params: { regionId }
		});
	}

	public async removeAlarmQueueFilter(alarmQueueFilterID: AlarmQueueFilterID): Promise<any> {
		const endpoint = `/AlarmQueueFilters/RemoveFilter/${alarmQueueFilterID.filterId}`;
		const regionId = alarmQueueFilterID.regionId > 0 ? alarmQueueFilterID.regionId : null;

		return await this.axios.get(endpoint, {
			params: { regionId }
		});
	}

	public async loadAvailableAlarmQueueFilters(): Promise<any> {
		return await this.axios.get("/AlarmQueueFilters/Available");
	}

	public async loadAlarmQueueFilterBounds(filterId: number): Promise<any> {
		return await this.axios.get(`/AlarmQueueFilters/Bounds/${filterId}`);
	}

	public async loadOutcomesForEvent(eventId: number): Promise<AxiosResponse<EventOutcomeResponse>> {
		return await this.axios.get(`/Outcomes/ForEvent/${eventId}`);
	}

	public async loadParkedNoteOutcomesForEvent(eventId: number): Promise<EventOutcomeDTO[]> {
		const response = await this.axios.get(`/Outcomes/ParkedNotesForEvent/${eventId}`);
		return response.data;
	}

	public async loadEventDetails(eventId: number): Promise<AxiosResponse<EventDetails>> {
		console.log("Loading event details! BaseURL=" + this.axios.defaults.baseURL);
		return await this.axios.get(`/Event/Details/${eventId}`);
	}

	public async loadEventRecordDetails(eventRecordId: number): Promise<EventRecordDetails> {
		const response = await this.axios.get(`/Event/EventRecordDetails/${eventRecordId}`);
		return response.data;
	}

	public async loadRoutingGroups(): Promise<any> {
		const response = await this.axios.get("/RoutingGroups");
		return response;
	}

	public async loadRoutingGroupsByEvent(eventId: number): Promise<any> {
		const response = await this.axios.get("/RoutingGroups/ByEvent/" + eventId);
		return response;
	}

	public async loadEventRecords(EventID: number, fromRecord: number): Promise<any> {
		const response = await this.axios.get("/EventRecords/" + EventID, {
			params: { fromRecord } /*,
			timeout: 5000 */
		});
		return response;
	}

	public async eventRecordAcknowledgement(EventRecordID: number): Promise<any> {
		const response = await this.axios.get(`/EventRecords/Acknowledge/${EventRecordID}`);
		return response;
	}
	public async eventRecordAllAcknowledgement(EventID: number): Promise<any> {
		const response = await this.axios.get(`/EventRecords/AcknowledgeAll/${EventID}`);
		return response;
	}

	public async eventShareRemove(eventID: number, eventUserID: any) {
		return await this.axios.get(`/EventShare/RemoveShare/${eventID}`, {
			params: {
				eventUserID
			}
		});
	}

	public async eventShareSearch(SearchTerm: string): Promise<any> {
		const response = await this.axios.get("/EventShare/Search", {
			params: {
				SearchTerm
			}
		});
		return response;
	}

	public async loadShareForEvent(eventId: number): Promise<any> {
		const { data } = await this.axios.get(`/EventShare/${eventId}`);
		return data;
	}

	public async loadHistoricalShareForEvent(eventId: number): Promise<any> {
		const { data } = await this.axios.get(`/EventShare/Historical/${eventId}`);
		return data;
	}

	public async loadExternalUserShareFlag(eventId: number): Promise<any> {
		const { data } = await this.axios.get(`/EventShare/ExternalShareAllowed/${eventId}`);
		return data;
	}

	public async loadGroupSummary(): Promise<any> {
		const response = await this.axios.get("/eventqueue/GroupSummary");
		return response;
	}

	public async loadGroupPermissions(groupID: number, regionUrl?: string): Promise<any> {
		const { data } = await this.axios.get("/groups/Permissions/" + groupID, { regionUrl });
		return data;
	}

	public async loadGroupOnTestEvent(groupID: number, regionUrl?: string): Promise<any> {
		const response = await this.axios.get("/OnTest/TestEvent/" + groupID, { regionUrl });
		return response.status === 204 ? null : response.data;
	}

	public async loadResponseOnTestEvent(responseID: number, regionUrl?: string): Promise<any> {
		const response = await this.axios.get(`/OnTest/ResponseTestEvent/${responseID}`, { regionUrl });
		return response.status === 204 ? null : response.data;
	}

	public async loadGroupOnTestRecords(eventID: number, fromRecord: number, regionUrl?: string): Promise<any> {
		const config: AxiosRequestConfig = {
			params: {
				fromRecord
			},
			regionUrl
		};

		const { data } = await this.axios.get("/OnTest/TestEventRecords/" + eventID, config);

		return data;
	}

	public async startTest(payload: any, regionUrl?: string): Promise<any> {
		const { data } = await this.axios.post("/OnTest/StartTest", payload, { regionUrl });
		return data;
	}

	public async extendTest(payload: any, regionUrl?: string): Promise<any> {
		const { data } = await this.axios.post("/OnTest/ExtendTest", payload, { regionUrl });
		return data;
	}

	public async endTest(eventId: number, regionUrl?: string): Promise<AxiosResponse<void>> {
		return await this.axios.get("/OnTest/EndTest/" + eventId, { regionUrl });
	}

	// TASKS
	//
	public async loadTasks(): Promise<any> {
		return await this.axios.get("/All");
	}

	public async triggeredTasks(taskID: number): Promise<any> {
		return (await this.axios.get(`/Tasks/Triggers/${taskID}`)).data;
	}

	public async getTaskTriggers(): Promise<any> {
		return await this.axios.get(`/GetTaskTriggers`);
	}

	public async triggeredTasksSave(
		taskID: number,
		tasks: any[],
		deleteExistingTriggers: boolean = true
	): Promise<void> {
		await this.axiosJson.post(`/Tasks/Triggers/${taskID}/${deleteExistingTriggers}`, tasks);
	}

	public async loadTaskTypes(): Promise<any> {
		return await this.axios.get("/Tasks/TaskTypes");
	}

	public async loadTasksForEvent(eventId: number): Promise<any> {
		return await this.axios.get(`/Tasks/${eventId}`);
	}

	public async fetchCallContacts(payload: any): Promise<any> {
		return await this.axios.post("/Tasks/CallList", payload);
	}

	public async fetchCallContactsWithRegion(
		contactRequest: CallContactRequest,
		regionUrl: string = undefined
	): Promise<any> {
		return await this.axios.post("/Tasks/CallList", contactRequest, { regionUrl });
	}

	public async searchTasks(searchPhrase: string): Promise<any> {
		return await this.axios.get(`/TaskSearch?search=${searchPhrase}`);
	}

	// save a task to task library - to a general "library" of tasks
	public async createTask(taskData: any): Promise<any> {
		const payload = {
			TaskTypeID: taskData.taskTypeID,
			TaskCategoryID: taskData.taskCategoryID,
			TaskData: JSON.stringify(taskData.taskData),
			TaskText: taskData.taskText
		};
		if (taskData.title) {
			payload["TaskText"] = taskData.title;
		}
		return await this.axios.post("/CreateTask", payload);
	}

	public async deleteTask(taskData: any) {
		return await this.axios.post("/DeleteTask", taskData);
	}

	// save a task to task library - to a general "library" of tasks
	public async updateTask(taskData: TaskLibrary): Promise<any> {
		const payload = {
			taskID: taskData.taskID,
			taskTypeID: taskData.taskTypeID,
			taskCategoryID: taskData.taskCategoryID,
			taskText: taskData.taskText,
			taskData: taskData.taskData,
			groupID: taskData.groupID
		};

		return await this.axiosJson.post("/UpdateTask", payload);
	}

	// save an event-specific task
	public async saveEventTask(task: any): Promise<any> {
		return await this.axiosJson.post("/Tasks", task);
	}

	public async completeTask(payload: CompleteTaskPayload): Promise<any> {
		return await this.axios.post("/Tasks/Complete", payload);
	}

	public async assignTask(payload: any): Promise<any> {
		return await this.axios.post("/Tasks/Assign", payload);
	}

	public async assignAllTask(payload: any): Promise<any> {
		return await this.axios.post("/Tasks/AssignAll", payload);
	}

	public async assignAllTasks(payload: any): Promise<any> {
		return await this.axios.post("/Tasks/AssignAll", payload);
	}

	public async createTaskCategory(payload: any): Promise<any> {
		return await this.axios.post("/CreateTaskCategory", payload);
	}

	public async updateActionTrigger(actionTrigger: ActionTrigger): Promise<void> {
		await this.axios.post("/Tasks/UpdateTaskTrigger", actionTrigger);
	}

	public async updateTaskCategory(payload: any): Promise<any> {
		return await this.axios.post("/UpdateTaskCategory", payload);
	}

	public async deleteTaskCategory(payload: any): Promise<any> {
		return await this.axios.post("/DeleteTaskCategory", payload);
	}

	// "Action Plans" (i.e. "Scripts")
	//
	public async loadActionPlans(
		groupId?: number,
		taskCategoryType?: TaskCategoryType
	): Promise<AxiosResponse<ActionPlan[]>> {
		var queryString = "";

		if (groupId) {
			queryString = `groupId=${groupId}`;
		}

		if (taskCategoryType) {
			queryString += `categoryId=${taskCategoryType}`;
		}

		if (queryString) {
			return await this.axios.get(`/Scripts?${queryString}`);
		}

		return await this.axios.get(`/Scripts`);
	}

	public async createActionPlan(payload: ActionPlanCreate): Promise<any> {
		return await this.axios.post("/CreateScript", payload);
	}

	public async deleteActionPlan(payload: any): Promise<any> {
		return await this.axios.post("/DeleteScript", payload);
	}

	public async updateActionPlan(payload: any): Promise<any> {
		return await this.axios.post("/UpdateScript", payload);
	}

	public async updateActionOrderInPlan(payload: any): Promise<any> {
		return await this.axios.post("/UpdateTaskOrderInScript", payload);
	}

	// load Task Library
	public async loadActionPlanActions(scriptId: number): Promise<any> {
		return await this.axios.get(`/ScriptTasks/${scriptId}`);
	}

	public async addActionToPlan(payload: ActionPayload): Promise<any> {
		return await this.axios.post("/AddTaskToScript", payload);
	}

	public async updateActionForPlan(payload: any): Promise<any> {
		return await this.axios.post("/UpdateTaskForScript", payload);
	}

	public async removeActionFromPlan(payload: ActionPayload): Promise<any> {
		return await this.axios.post("/RemoveTaskFromScript", payload);
	}

	public async removeChildActionFromPlan(triggerId: string): Promise<any> {
		return await this.axios.delete(`/RemoveNestedAction/${triggerId}`);
	}

	public async loadTaskCategories(taskCategoryType?: TaskCategoryType): Promise<AxiosResponse<TaskCategory[]>> {
		if (taskCategoryType) {
			return await this.axios.get(`/TaskCategories?taskCategoryTypeId=${taskCategoryType}`);
		}
		return await this.axios.get("/TaskCategories");
	}
	//
	// END "Action Plans" (i.e. "Scripts")

	public async loadTasksByCategory(taskCategoryID: number): Promise<any> {
		return await this.axios.get(`/TasksByCategory/${taskCategoryID}`);
	}

	public async loadTaskCategoryTypes(): Promise<any> {
		const { data } = await this.axios.get("/TaskCategoryTypes");
		return data;
	}

	// DispatchSetup
	public async loadGuards(): Promise<any> {
		const { data } = await this.axios.get("/DispatchSetup/AllGuards");
		return data;
	}

	// Used in dispatch - loads all the guard types from the database
	public async loadGuardTypes(): Promise<any> {
		const { data } = await this.axios.get("/DispatchSetup/AllGuardTypes");
		return data;
	}

	public async loadGuardsForFilter(alarmQueueFilterID: number): Promise<any> {
		const { data } = await this.axios.get(`/DispatchSetup/GuardsForFilter/${alarmQueueFilterID}`);
		return data;
	}

	public async addNewGuard(guard: Guard): Promise<number> {
		const { data } = await this.axios.post("/DispatchSetup/AddGuard", guard);
		return data;
	}

	public async assignGuardToZone(data: any): Promise<any> {
		return await this.axios.post("/DispatchSetup/PatrolZones/AssignGuard", data);
	}

	public async removeGuardFromZone(data: any): Promise<any> {
		return await this.axios.post("/DispatchSetup/PatrolZones/RemoveGuard", data);
	}

	public async updateGuard(data: any): Promise<any> {
		return await this.axios.post("/DispatchSetup/UpdateGuard", data);
	}

	public async deleteGuard(dispatchGuardID: number): Promise<any> {
		return await this.axios.get(`/DispatchSetup/DeleteGuard/${dispatchGuardID}`);
	}

	public async updateMultipleGuards(data: UpdateMultipleGuardsDTO): Promise<any> {
		return await this.axios.post("/DispatchSetup/UpdateMultipleGuards", data);
	}

	public async addMultipleGuards(data: AddMultipleGuardsDTO): Promise<AxiosResponse<void>> {
		return await this.axios.post("/DispatchSetup/AddMultipleGuards", data);
	}

	public async uploadGuardsFile(fileToUpload: File, onProgressUpdate: (newProgress: number) => void): Promise<any> {
		const formData = new FormData();
		formData.append("file", fileToUpload, fileToUpload.name);

		const requestConfig = {
			onUploadProgress: (progressEvent: ProgressEvent) => {
				// When axios sends us an upload progress event, invoke our callback with the % figure
				let percentComplete = Math.round((progressEvent.loaded * 100) / progressEvent.total);
				onProgressUpdate(percentComplete);
			}
		};

		return await this.axiosJson.post("/DispatchSetup/ProcessGuardsCsvFile", formData, requestConfig);
	}

	// Dispatch
	public async loadGuardsForEvent(
		eventId: number,
		location: string,
		includeGuardsWithMatchingFilter: boolean = false
	): Promise<any> {
		const { data } = await this.axios.get(`/Dispatch/ForLocation/${eventId}`, {
			params: {
				location,
				includeGuardsWithMatchingFilter
			}
		});
		return data;
	}

	public async loadDispatchedGuardsForEvent(eventId: number): Promise<any> {
		return await this.axios.get(`/Dispatch/DispatchStatus/${eventId}`);
	}

	public async dispatchGuard(dispatchGuardRequest: {
		guardID;
		eventID;
		status;
		location;
	}): Promise<AxiosResponse<any>> {
		return await this.axios.post("/Dispatch", dispatchGuardRequest);
	}

	public async loadGuardTypePref() {
		const response = await this.axios.get("/Dispatch/GetGuardTypePref");
		return response.data;
	}

	public async loadGuardTypeByIconPref() {
		const response = await this.axios.get("/Dispatch/GetGuardTypeByIconPref");
		return response.data;
	}

	public async updateGuardTypeCache(guard: Guard) {
		await this.axiosJson.post("/DispatchManage/UpdateGuardTypeCache", guard);
	}

	// Gets the list of groups the current user can access
	public async loadUserGroups(): Promise<any> {
		const response = await this.axios.get("/Groups");
		return response.data;
	}

	/**
	 * Loads a specified group by ID.
	 * @param groupId The ID of the group.
	 * @param regionUrl Optional, the URL of the region to hit.
	 * @returns The group details.
	 */
	public async loadGroup(groupId: number, regionUrl?: string): Promise<Group> {
		const response = await this.axios.get(`/Groups/${groupId}`, { regionUrl });
		return response.data;
	}

	public async loadUserZone(): Promise<any> {
		const response = await this.axios.get("/Groups/UsersGroup");
		return response.data;
	}

	/**
	 * @deprecated This method is deprecated as it can cause performance issues on large systems, use the Groups API
	 * instead.
	 */
	public async loadGroupsTree(multiTenant?: boolean): Promise<any> {
		if (multiTenant) {
			const response = await this.axios.get("/Groups/Tree?multiTenant=true");
			return response.data;
		}

		const response = await this.axios.get("/Groups/Tree");
		return response.data;
	}

	/**
	 * @deprecated This method is deprecated as it can cause performance issues on large systems, use the Groups API
	 * instead.
	 */
	public async loadGroupsTreeWithRegion(multiTenant?: boolean, regionUrl: string = undefined): Promise<any> {
		console.log("LoadGroupsTreeWithRegion: " + regionUrl);
		const config: AxiosRequestConfig = {
			params: {
				multiTenant
			},
			regionUrl
		};

		const response = await this.axios.get("/Groups/Tree", config);
		return response.data;
	}

	public async loadGroupsList(): Promise<any> {
		const response = await this.axios.get("/Groups");
		return response.data;
	}

	public async subgroups(parentGroupId: number): Promise<number[]> {
		const response = await this.axiosJson.get("/groups/subgroups", {
			params: {
				parentGroupId
			}
		});
		return response.data;
	}

	public async groupsToRoot(groupId: number): Promise<any> {
		const response = await this.axiosJson.get("/groups/toroot", {
			params: {
				groupId
			}
		});
		return response.data;
	}

	public async searchGroup(SearchTerm: string): Promise<any> {
		const response = await this.axios.get("/Groups/Search", {
			params: {
				SearchTerm
			}
		});
		return response.data;
	}

	public async searchGroupFull(cancelToken: any, SearchTerm: string): Promise<any> {
		console.log(cancelToken);

		const response = await this.axios.get("/Groups/SearchFull", {
			params: {
				SearchTerm
			},
			cancelToken
		});
		return response.data;
	}

	public async loadPatrolZones(): Promise<any> {
		const { data } = await this.axios.get("/DispatchSetup/PatrolZones");
		return data;
	}

	public async addPatrolZone(zone: any): Promise<any> {
		const { data } = await this.axios.post("/DispatchSetup/PatrolZones/Add", zone);
		return data;
	}

	public async updatePatrolZone(zone: any): Promise<any> {
		const { data } = await this.axios.post("/DispatchSetup/PatrolZones/Update", zone);
		return data;
	}

	public async deletePatrolZone(PatrolZoneID: number): Promise<any> {
		await this.axios.get(`/DispatchSetup/PatrolZones/Delete/${PatrolZoneID}`);
	}

	// Alarm Unmasking
	public async unmaskArea(groupId: number, reMaskAt: Date = null, regionUrl?: string): Promise<void> {
		const request: UnmaskAreaRequest = {
			groupId: groupId,
			reMaskAt: reMaskAt
		};
		await this.axiosJson.put("/Masking/UnmaskArea", request, { regionUrl });
	}

	// Alarm Masking
	public async maskArea(
		groupId: number,
		reason: MaskReason = null,
		unMaskAt: Date = null,
		regionUrl?: string
	): Promise<void> {
		const request: MaskAreaRequest = {
			disarmReasonId: !reason ? 0 : reason.disarmReasonID,
			groupId: groupId,
			unMaskAt: unMaskAt
		};
		await this.axiosJson.put("/Masking/MaskArea", request, { regionUrl });
	}

	public async loadMaskReasonsForAlarm(groupID: number, regionUrl?: string): Promise<any> {
		return (await this.axios.get(`/Masking/Reasons/Alarm/${groupID}`, { regionUrl })).data;
	}

	public async loadMaskReasonsForGroup(groupID: number, regionUrl: string): Promise<any> {
		return (await this.axios.get(`/Masking/Reasons/Group/${groupID}`, { regionUrl })).data;
	}

	public async maskAlarm(maskRequest: any, regionUrl?: string): Promise<any> {
		return (await this.axios.post("/Masking/MaskAlarm", maskRequest, { regionUrl })).data;
	}

	public async unmaskAlarm(eventID: number, responseID: number, regionUrl?: string): Promise<any> {
		await this.axios.post("/Masking/UnmaskAlarm", { eventID, responseID }, { regionUrl });
	}

	public async loadMaskedAlarmsForGroup(groupID: number, regionUrl?: string): Promise<any> {
		return await this.axios.get(`/Masking/Masked/Alarms/${groupID}`, { regionUrl });
	}

	/* public async eventSearch(filters: any): Promise<any> {
	const { data } = await this.axios.post('/eventSearch', filters);
	return data;
  } */

	public async eventSearch(filters: any): Promise<any> {
		const { data } = await this.axiosJson.post("/EventReports/Search", filters);
		return data;
	}

	public async eventTags(): Promise<Tag[]> {
		return (await this.axios.get("/Event/Tags")).data;
	}

	public async loadEventNotes(eventId: number): Promise<any> {
		const { data } = await this.axios.get(`/Notes/ForEvent/${eventId}`);
		return data;
	}

	public async matrixGet(eventId): Promise<any> {
		return (await this.axios.get("/Matrix/" + eventId)).data;
	}

	public async matrixSave(payload: any): Promise<any> {
		return this.axios.post("/Matrix", payload);
	}

	public async loadSearchableEventTypes(): Promise<any> {
		const { data } = await this.axios.get("/EventReports/SearchableEventTypes");
		return data;
	}

	public async loadAvailableDeviceTypes(): Promise<any> {
		const { data } = await this.axios.get("/EventReports/DeviceTypes");
		return data;
	}

	public async loadAvailableEventCategories(): Promise<any> {
		const { data } = await this.axios.get("/EventReports/Categories");
		return data;
	}

	public async loadAvailableEventOperators(): Promise<any> {
		const { data } = await this.axios.get("/EventReports/Operators");
		return data;
	}

	public async loadAlarmTypes(serverTypeID: number): Promise<any> {
		const { data } = await this.axios.get(`/EventReports/EventTypes/${serverTypeID}`);
		return data;
	}

	public async loadAreaNotesForEvent(eventId: number): Promise<any> {
		const { data } = await this.axios.get(`/notes/ForEvent/${eventId}`);
		return data;
	}

	public async loadAreaNotesForGroup(groupId: number): Promise<any> {
		const { data } = await this.axios.get(`/Notes/ForGroup/${groupId}`);
		return data;
	}

	public async addAreaNote(note: any): Promise<any> {
		return await this.axios.post("/notes/Add", note);
	}

	public async editAreaNote(note: any): Promise<any> {
		return await this.axios.post("/notes/Edit", note);
	}

	public async deleteAreaNote(noteId: number): Promise<any> {
		const { data } = await this.axios.delete("/notes/Delete", {
			params: {
				noteId
			}
		});
		return data;
	}

	public async updateMatrixCell(data: any): Promise<any> {
		return await this.axios.post("/Matrix/Cell", data);
	}

	public async sessionGet(resourceId: number, eventId: number, regionUrl?: string) {
		const config: AxiosRequestConfig = {
			params: {
				eventId
			},
			regionUrl
		};

		return await axiosInstance.get(`/session/${resourceId}`, config);
	}

	public async saveMatrix(data: any): Promise<any> {
		return await this.axios.post("/Matrix", data);
	}

	public async loadAllClipCellsForEvent(eventID: number): Promise<any> {
		return await this.axios.get(`/Matrix/AllClips/${eventID}`);
	}

	public async serverLogout(): Promise<any> {
		return await this.axios.get("/Login/Logout");
	}

	public async loadAlarmTags(): Promise<any> {
		return await this.axios.get("/AlarmQueueFilters/AlarmTags");
	}

	public async createAlarmQueueFilter(data: any): Promise<any> {
		return await this.axiosJson.post("/AlarmQueueFilters/CreateFilter", data);
	}

	public async editAlarmQueueFilter(data: any): Promise<any> {
		return await this.axiosJson.post("/AlarmQueueFilters/EditFilter", data);
	}

	public async loadNotificationTemplate(payload: any): Promise<any> {
		return await this.axios.post("/Notification/Template", payload);
	}

	public async loadTemplateDetails(templateId: number): Promise<any> {
		return await this.axios.get(`/Notification/Template/Details/${templateId}`);
	}

	public async acceptHighPriority(request: HighPriorityAcceptRequest): Promise<AxiosResponse<null>> {
		// Needs to process in local region, DS handles if it needs to process elsewhere
		return this.axiosJson.post<null>("/HighPriority/Accept", request, {
			baseURL: api.getOriginalApiUrl
		});
	}

	public async ignoreHighPriority(request: HighPriorityIgnoreRequest): Promise<AxiosResponse<null>> {
		// Needs to process in local region, DS handles if it needs to process elsewhere
		return this.axiosJson.post<null>("/HighPriority/Ignore", request, {
			baseURL: api.getOriginalApiUrl
		});
	}

	public async fetchThresholdHighPriority(): Promise<AxiosResponse<number>> {
		return await this.axios.get("/HighPriority/Threshold");
	}

	public async loadEventShareInvites(): Promise<any> {
		return await this.axios.get("EventShare/Invites");
	}

	public async acceptEventShareInvite(eventUserID: number): Promise<any> {
		return await this.axios.get(`EventShare/Accept/${eventUserID}`);
	}

	public async ignoreEventShareInvite(eventUserID: number): Promise<any> {
		return await this.axios.get(`EventShare/Ignore/${eventUserID}`);
	}

	public async addAdhocEventShare(payload: any): Promise<any> {
		return await this.axios.post("/EventShare/InviteAdhoc", payload);
	}

	public async shareEventWithUser(payload: any): Promise<any> {
		return await this.axios.post("/EventShare/InviteUser", payload);
	}

	public async shareEventWithGroup(payload: any): Promise<any> {
		return await this.axios.post("/EventShare/InviteGroup", payload);
	}

	public async deleteAlarmQueueFilter(filterId: number): Promise<any> {
		return await this.axios.get(`/AlarmQueueFilters/DeleteFilter/${filterId}`);
	}

	public async loadServerEventTypes(
		serverType: number,
		serverTypeGroupID: number | null,
		pageSize?: number | null,
		filter?: string | null,
		regionId: number | null = null
	): Promise<any> {
		return await this.axios.get(`/servers/EventTypes/${serverType}`, {
			params: {
				groupID: serverTypeGroupID,
				pageSize,
				filter,
				regionId
			}
		});
	}

	public async searchGroups(params): Promise<any> {
		return await this.axios.get("/groups/search", {
			params
		});
	}

	public async loadMapLayersByGroupID(
		groupId: number,
		regionId: number | null = null
	): Promise<AxiosResponse<MapLayerData[]>> {
		return await this.axios.get(`/MapLayers/ByGroup/${groupId}`, {
			params: {
				regionId
			}
		});
	}

	public async loadMapLayerItemsByMapLayer(mapLayerId: number): Promise<any> {
		return await this.axiosJson.post("/MapLayers/MapItems/ByLayer", mapLayerId);
	}

	public async loadMapUnlinkedItemsByGroup(groupId: number): Promise<any> {
		return await this.axiosJson.get(`/MapLayers/MapItems/Unlinked/?groupId=${groupId}`);
	}

	public async loadPlottedMapLayerItemsByGroupID(groupID: Number): Promise<MapItemData[]> {
		const response = await this.axios.get(`/MapLayers/MapItems/ByGroup/Plotted/${groupID}`);
		return response.data;
	}

	public async loadMapItemLocationIdentifiers(): Promise<MapItemLocationIdentifierOption[]> {
		const response = await this.axios.get("/MapLayers/MapItemLocationIdentifiers");
		return response.data;
	}

	public async responseGet(responseId: number, regionUrl?: string): Promise<any> {
		const { data } = await this.axios.get(`/Responses/${responseId}`, { regionUrl });
		return data;
	}

	public async responsesByArea(request: ResponseSearchRequest): Promise<Response[]> {
		const regionUrl = request.RegionURL;
		return (await this.axios.get("/Responses/ByArea/" + request.GroupID + "/" + request.Query, { regionUrl })).data;
	}

	public async responsesDisarmed(regionUrl?: string): Promise<any> {
		return (await this.axios.get("/Responses/Disarmed/", { regionUrl })).data;
	}

	public async responseSearch(cancelToken: any, searchTerm: string, regionUrl?: string): Promise<any> {
		const config: AxiosRequestConfig = {
			params: {
				search: searchTerm
			},
			cancelToken,
			regionUrl
		};

		return (await this.axios.get("/Responses/Search", config)).data;
	}

	public async responseHistory(responseId: number, regionUrl?: string): Promise<AlarmPointHistory[]> {
		return (await this.axios.get(`/EventRecords/AlarmPointHistory/${responseId}`, { regionUrl })).data;
	}

	public async accessControlHistory(responseId: number): Promise<any> {
		const avoidInterceptors = axios.create({
			baseURL: axiosInstance.defaults.baseURL,
			withCredentials: true,
			headers: {
				Accept: "application/json, text/javascript, */*; q=0.01"
			}
		});
		avoidInterceptors.interceptors.response.use(undefined, (error: any) => {
			console.log(error);
		});

		return (
			await avoidInterceptors.get("/AccessControl/History/Latest", {
				params: {
					responseId
				}
			})
		).data;
	}

	public async fetchServers(groupId: number, pageSize?: number, filter?: string): Promise<any> {
		return await this.axios.get(`/servers?groupId=${groupId}`, {
			params: {
				pageSize,
				filter
			}
		});
	}

	public async getServers(
		params: PaginatedSearchQueryParams = null,
		cancelTokenSource: CancelTokenSource = null
	): Promise<AxiosResponse<ServerDetails[]>> {
		return await this.axios.get(`/servers/GetServerList`, { params, cancelToken: cancelTokenSource?.token });
	}

	public async getServersWithEnabledDeviceTypes(
		params: ServersWithEnabledDeviceTypesParams
	): Promise<AxiosResponse<ServerDetails[]>> {
		return await this.axios.get(`/servers/ByDeviceTypesEnabled`, { params });
	}

	public async getServer(serverId: number): Promise<AxiosResponse<ServerDetails>> {
		return await this.axios.get(`/servers/${serverId}`);
	}

	public async fetchServersByGroup(payload: any): Promise<any> {
		return await this.axios.get(`/servers/ServersByGroup/${payload.groupId}/${payload.page}/${payload.pageSize}`);
	}

	public async fetchServerLineProfiles(): Promise<AxiosResponse<ServerLineProfile[]>> {
		return await this.axios.get("/servers/LineProfiles");
	}

	public async fetchNotificationServers(): Promise<any> {
		return await this.axios.get("/servers/Notification");
	}

	public async getNotificationServersByName(searchTerm: string): Promise<any> {
		return await this.axios.get(`/servers/GetNotificationServersByName/${searchTerm}`);
	}

	public async fetchNotificationServersByGroup(groupId: number): Promise<AxiosResponse<ServerDetails[]>> {
		return await this.axios.get(`/servers/Notification/${groupId}`);
	}

	public async fetchNotificationTemplates(serverId: number): Promise<any> {
		return await this.axios.get(`/Notification/Template/${serverId}`);
	}

	public async fetchNotificationTemplatesByTitle(payload: QueryNotificationServers) {
		return await this.axios.get(`/Notification/Template/TemplateByTitle/${payload.serverId}/${payload.searchTerm}`);
	}
	public async fetchNotificationContacts(groupId: number, searchTerm: string): Promise<any> {
		return await this.axios.get(`/Notification/Template/Contacts/${groupId}/${searchTerm}`);
	}

	public async getMatrixDeviceDetails(deviceIds: number[], eventId: number, latLong: string) {
		const { data } = await this.axios.post("/DeviceDetails", {
			deviceIds,
			eventId,
			latLong
		});

		return data;
	}

	public async loadAvailableReports(apiUrl: string): Promise<any> {
		return await this.axios.get("/Reports/AvailableReports", {
			baseURL: apiUrl
		});
	}

	public async runReport(apiUrl: string, url: string, payload: any): Promise<any> {
		return await this.axiosJsonDirectInstance.post(`/Reports/GetReport${url}`, payload, {
			baseURL: apiUrl
		});
	}

	public async DownloadReport(apiUrl: string, url: string, payload: any): Promise<any> {
		return await this.axiosJson.post(`/Reports/DownloadReport${url}`, payload, {
			baseURL: apiUrl
		});
	}

	public async getReportServiceEndpoint(): Promise<any> {
		return await this.axios.get("/Reports/GetReportsDataServiceEndpoint");
	}

	public async loadReportTypes(url: string): Promise<any> {
		return await this.axios.get("/Reports/GetReportTypes", {
			baseURL: url
		});
	}

	public async GetReportTypesTitles(): Promise<any> {
		return await this.axios.get("/Reports/GetReportTypesTitles");
	}

	public async setCustomTitle(payload: ReportTitles, url: string): Promise<any> {
		return await this.axios.post("/Reports/InsertCustomTitle", payload, {
			baseURL: url
		});
	}

	public async queryFilter(endpoint: string, filter: string, url: string): Promise<any> {
		return await this.axios.get(`/Reports/${endpoint}/${filter}`, {
			baseURL: url
		});
	}

	public async alarmPointsByAreas(endpoint: string, payload: any, url: string): Promise<any> {
		return await this.axiosJson.post(`/Reports/${endpoint}`, payload, {
			baseURL: url
		});
	}

	/**
	 * Heartbeats to the local region.
	 */
	public async heartbeatToLocalServer(): Promise<AxiosResponse<number>> {
		return await this.heartbeatToServer(null);
	}

	/**
	 * Heartbeats to a specified regional data service.
	 * @param regionUrl The region url, or null if local.
	 */
	public async heartbeatToServer(regionUrl?: string): Promise<AxiosResponse<number>> {
		return await this.axiosJsonDirectInstance.get("/session/HeartbeatLoginSession", {
			baseURL: regionUrl || this.getOriginalApiUrl
		});
	}

	public async loadUserList(
		page: number,
		pageSize: number,
		filter: string,
		cancelToken?: CancelToken,
		includeBlankUsernames?: boolean
	): Promise<UserDetails[]> {
		const url: string = `/users/GetUserList/${page}/${pageSize}/${includeBlankUsernames}/${filter}`;
		const response = await this.axios.get(url, { cancelToken });
		return response?.data;
	}

	public async loadContactsList(regionUrl?: string): Promise<AxiosResponse<any>> {
		return await this.axios.get("/users/Contacts", { regionUrl });
	}

	public async getContacts(
		page: number = 1,
		pageSize: number = 30,
		filter: string = ""
	): Promise<AxiosResponse<ContactUser[]>> {
		return await this.axios.get(`users/Contacts/${page}/${pageSize}/${filter}`);
	}

	public async getUserById(userId: number): Promise<any> {
		const { data } = await this.axios.get(`/users/GetUser/${userId}`);
		return data;
	}

	public async getContactRole(userId: number): Promise<any> {
		const { data } = await this.axios.get(`/users/${userId}/ContactRole`);
		return data;
	}

	public async getContactRoleGroups(userId: number): Promise<any> {
		const { data } = await this.axios.get(`/users/ContactRoleGroups/${userId}`);
		return data;
	}

	public async updateContactRole(updatedContactRole: any): Promise<any> {
		const { data } = await this.axios.put("/users/ContactRole", updatedContactRole);
		return data;
	}

	public async deleteContactRole(contactRoleId: any): Promise<any> {
		return await this.axios.delete(`/users/ContactRole/${contactRoleId}`);
	}

	public async createContactRole(newContactRole: any): Promise<any> {
		const { data } = await this.axios.post("/users/ContactRole", newContactRole);
		return data;
	}

	public async loadContactRoleList(): Promise<any> {
		return await this.axios.get("/users/ContactRoles");
	}

	public async getAllActiveUsers(): Promise<User[]> {
		const { data } = await this.axios.get("/Messages/GetListOfActiveUsers");
		return data;
	}

	public async updateUser(updatedUser: any): Promise<any> {
		return await this.axiosJson.post("/users/UpdateUser", updatedUser);
	}

	public async updateRole(params): Promise<any> {
		return await this.axios.post("/users/UpdateRole", params);
	}

	public async deleteUser(userId: number): Promise<any> {
		return await this.axios.delete(`/users/DeleteUser/${userId}`);
	}

	public async createUser(newUser: any): Promise<any> {
		return await this.axios.post("/users/CreateUser", newUser);
	}

	public async getUserRoles(tenancyGroupId: number): Promise<any> {
		return (await this.axios.get(`/UserSetup/UserRoles/${tenancyGroupId}`)).data;
	}

	public async saveUserRole(userRole: any, tenancyGroupId: number): Promise<number> {
		return (await this.axiosJson.post(`/UserSetup/UserRole?tenancyGroupId=${tenancyGroupId}`, userRole)).data;
	}

	public async deleteUserRole(userRoleId: number, tenancyGroupId: number): Promise<void> {
		await this.axiosJson.delete(`/UserSetup/UserRole/Delete/${userRoleId}?tenancyGroupId=${tenancyGroupId}`);
	}

	public async getUserGroups(tenancyGroupId: number): Promise<any> {
		return (await this.axios.get(`/UserSetup/UserGroups/${tenancyGroupId}`)).data;
	}

	public async saveUserGroup(userGroup: any, tenancyGroupId: number): Promise<number> {
		const { data } = await this.axiosJson.post(`/UserSetup/UserGroup?tenancyGroupId=${tenancyGroupId}`, userGroup);
		return data;
	}

	public async deleteUserGroup(userGroupId: number, tenancyGroupId: number): Promise<void> {
		await this.axiosJson.delete(`/UserSetup/UserGroup/Delete/${userGroupId}?tenancyGroupId=${tenancyGroupId}`);
	}

	public async getUserGroup(): Promise<any> {
		const { data } = await this.axios.get("/users/GetUserGroup");
		return data;
	}

	public async permissionsGet(): Promise<any> {
		return (await this.axios.get("/UserSetup/Permissions")).data;
	}

	public async domainsGet(): Promise<Domain[]> {
		return (await this.axios.get("/UserSetup/Domains")).data;
	}

	public async domainGroupsGet(): Promise<AxiosResponse<DomainGroup[]>> {
		return (await this.axios.get("/DomainGroupMapping")).data;
	}

	public async domainGroupsPost(domainGroup: DomainGroup): Promise<AxiosResponse<DomainGroup>> {
		return (await this.axios.post("/DomainGroupMapping", domainGroup)).data;
	}

	public async domainGroupUpdate(
		domainId: number,
		userGroupId: number,
		domainGroup: DomainGroup
	): Promise<AxiosResponse<DomainGroup>> {
		return (
			await this.axios.put(`/DomainGroupMapping?domainId=${domainId}&userGroupId=${userGroupId}`, domainGroup)
		).data;
	}

	public async domainGroupsDelete(domainGroup: DomainGroup): Promise<AxiosResponse<DomainGroup>> {
		return (await this.axios.delete("/DomainGroupMapping", { data: domainGroup })).data;
	}

	public async getPermissionsForUserGroups(userGroupIds: number[]): Promise<any[]> {
		const { data } = await this.axiosJson.post("/UserSetup/PermissionsForUserGroups", userGroupIds);
		return data;
	}

	public async loadAllFeatures(tenantId: number): Promise<any> {
		return await this.axios.get("/Features?featureGroup=" + tenantId);
	}

	public async toggleFeatures(features: FeatureTreeNode[], tenantId: number): Promise<AxiosResponse<boolean>> {
		return await this.axiosJson.put("/Features?featureGroup=" + tenantId, features);
	}

	public async loadJsonConfig(): Promise<any> {
		return await this.axios.get("/Features/Config");
	}

	public async addArea(area: any): Promise<any> {
		return await this.axiosJson.post("/groups/AddGroup", area);
	}

	public async updateArea(area: any): Promise<any> {
		return await this.axiosJson.post("/groups/UpdateGroup", area);
	}

	public async getArea(areaId: number): Promise<any> {
		const { data } = await this.axios.get(`/groups/${areaId}`);
		return data;
	}

	public async getAreaList(cancelToken: CancelToken): Promise<any> {
		const { data } = await this.axios.get(`/groups/GetGroups`, {
			cancelToken
		});
		return data;
	}

	public async deleteArea(areaId: number): Promise<any> {
		return await this.axios.get(`/groups/DeleteGroup/${areaId}`);
	}

	public async getReportTemplates(reportKey: string): Promise<any> {
		return await this.axios.get("/Reports/Templates/" + reportKey);
	}

	public async getAvailableReportTemplatesForArea(areaId: number): Promise<any> {
		return await this.axios.get(`/Reports/AvailableReportTemplatesForArea?AreaId=${areaId}`);
	}

	public async getActivityLog(queryStr): Promise<any> {
		let url = "/ActivityLog/GetList";
		if (queryStr && queryStr.length > 0) {
			url += queryStr;
		}
		return await this.axios.get(url);
	}

	public async addActivityLog(payload: any): Promise<any> {
		return await this.axios.post(`/ActivityLog/Add`, payload);
	}

	public async editActivityLog(payload: any): Promise<any> {
		return await this.axios.post(`/ActivityLog/Edit`, payload);
	}

	public async hideActivityLog(activityLogId: number): Promise<any> {
		return await this.axios.get(`/ActivityLog/Hide/${activityLogId}`);
	}

	public async unHideActivityLog(activityLogId: number): Promise<any> {
		return await this.axios.get(`/ActivityLog/Unhide/${activityLogId}`);
	}

	public async getActivityLogOutcomes(areaId: null | boolean): Promise<any> {
		let url = "/Outcomes/GetActivityLogOutcomes";

		if (areaId) {
			url += "?areaID=" + areaId;
		}

		return await this.axios.get(url);
	}

	public async getActivityLogEntry(activityLogId: number): Promise<any> {
		return await this.axios.get(`/ActivityLog/${activityLogId}`);
	}

	public async enableArea(areaId: number): Promise<any> {
		return await this.axios.get(`/groups/enableGroup/${areaId}`);
	}

	public async disableArea(areaId: number): Promise<any> {
		return await this.axios.get(`/groups/DisableGroup/${areaId}`);
	}

	public async fetchFeaturesList(): Promise<any> {
		return await this.axios.get("/Features/getFeaturesList");
	}

	public async getTimeZones(): Promise<any> {
		const { data } = await this.axios.get("/groups/TimeZones");
		return data;
	}

	public async fetchDynamicDataList(): Promise<any> {
		return await this.axios.get("/Tasks/GetDynamicDataList");
	}

	public async fetchDynamicData(payload: any): Promise<any> {
		return await this.axios.post(`/Tasks/GetDynamicData`, payload);
	}

	public async queryDynamicDataByIdAndFilter(ddlType: number, filter: string): Promise<any> {
		return await this.axios.get(`/Tasks/QueryDynamicDataByIdAndFilter/${ddlType}/${filter}`);
	}

	public async getDynamicDataById(dynamicDataIds: number[]) {
		return await this.axios.post("/Tasks/GetDynamicDataById", { dynamicDataIds: dynamicDataIds });
	}

	public async putEventOnTest(payload: any): Promise<any> {
		return await this.axiosJson.post("OnTest/PutEventOnTest", payload);
	}

	public async changePassword(payload: any): Promise<any> {
		const { data } = await this.axiosJson.post(`/users/ChangePassword`, payload);
		return data;
	}

	public async checkPasswordComplexity(payload: PasswordComplexityRequest): Promise<any> {
		const { data } = await this.axiosJson.post(`/users/CheckPasswordComplexity`, payload);
		return data;
	}

	public async getAccountCreationStatus(): Promise<any> {
		const response = await this.axios.get(`/register/AccountCreationAllowed`);
		return response && response.data ? response.data : null;
	}

	public async register(payload: any): Promise<any> {
		const { data } = await this.axiosJson.post(`/register`, payload);
		return data;
	}

	public async serverTypeEvents(
		page: number = 1,
		size: number = 30,
		includeHidden: boolean = false,
		query: string
	): Promise<AxiosResponse<ServerTypeEvent[]>> {
		return await this.axiosJson.get(`/servertypeevents/${page}/${size}/${includeHidden}/${query ?? ""}`);
	}

	public async getServerTypes(includeHidden: boolean = false): Promise<AxiosResponse<ServerType[]>> {
		return await this.axiosJson.get(`/servertypeevents/serverTypes/${includeHidden}`);
	}

	public async serverTypeEventUpdate(serverTypeEvent: ServerTypeEvent): Promise<number> {
		return (await this.axiosJson.post("/servertypeevents", serverTypeEvent)).data;
	}

	public async serverTypeEventDelete(serverTypeEvent: ServerTypeEvent): Promise<void> {
		await this.axiosJson.delete("/servertypeevents", {
			params: {
				serverTypeId: serverTypeEvent.serverTypeID,
				eventNum: serverTypeEvent.eventNum
			}
		});
	}

	public async responseAlarmTypes(): Promise<ResponseAlarmType[]> {
		return (await this.axiosJson.get("/Responses/ResponseAlarmTypes")).data;
	}

	public async addServer(newServer: ServerDetails): Promise<AxiosResponse<number>> {
		return await this.axios.post("/servers/AddServer", newServer);
	}

	public async updateServer(updatedServer: any): Promise<any> {
		return await this.axios.post("/servers/UpdateServer", updatedServer);
	}

	public async deleteServer(serverId: number): Promise<any> {
		return await this.axios.delete(`/servers/DeleteServer/${serverId}`);
	}

	public async addDevice(newDevice: any): Promise<any> {
		return await this.axios.post("/devices/AddDevice", newDevice);
	}

	public async updateDevice(updatedDevice: any): Promise<any> {
		return await this.axios.post("/devices/UpdateDevice", updatedDevice);
	}

	public async deleteDevice(deviceId: number): Promise<any> {
		return await this.axios.delete(`/devices/DeleteDevice/${deviceId}`);
	}

	public async getDevicesByExtraValues(
		payload: GetDevicesByExtraValuesPayload
	): Promise<AxiosResponse<DeviceDetails[]>> {
		return await this.axios.post("/devices/GetByExtraValues", payload);
	}

	public async getDevicesForAllGroupsByType(
		params: GetDevicesByTypeParams,
		cancelTokenSource: CancelTokenSource = null
	) {
		const { deviceTypeId, serverId, page, pageSize, searchTerm, selectOutputs, includeReferenceShots } = params;
		let endpoint = `/Devices/ByType/${deviceTypeId}?page=${page}&pageSize=${pageSize}`;

		if (serverId) {
			endpoint += `&serverId=${serverId}`;
		}

		if (searchTerm) {
			endpoint += `&searchTerm=${searchTerm}`;
		}

		if (selectOutputs === true || selectOutputs === false) {
			endpoint += `&selectOutputs=${selectOutputs}`;
		}

		if (includeReferenceShots) {
			endpoint += `&includeReferenceShots=true`;
		}

		return await this.axiosJson.get(endpoint, { cancelToken: cancelTokenSource?.token });
	}

	public async getDevicesByType(
		groupId: number,
		deviceTypeId: number,
		filter: string | null | undefined
	): Promise<AxiosResponse<DeviceDetails[]>> {
		let uri = `/Devices/GetDevicesByType/${groupId}/${deviceTypeId}`;
		if (filter) {
			uri += `?filter=${filter}`;
		}

		return await this.axiosJson.get(uri);
	}

	public async getDeviceTypeCounts(serverId: number): Promise<AxiosResponse<DeviceTypeCounts>> {
		let endpoint = "/Devices/TypeCounts";

		if (serverId) {
			endpoint += `?serverId=${serverId}`;
		}

		return await this.axiosJson.get(endpoint);
	}

	public async getDeviceTypes(
		params: PaginatedSearchQueryParams = null,
		cancelTokenSource: CancelTokenSource = null
		): Promise<any> {
		const { data } = await this.axios.get(`/servers/GetServerTypes`, { params, cancelToken: cancelTokenSource?.token });
		return data;
	}

	public async updateServerType(updatedServerType: ServerType): Promise<AxiosResponse<ServerType>> {
		return await this.axios.put("/servers/UpdateServerType", updatedServerType);
	}

	public async getApplianceList(): Promise<any> {
		const { data } = await this.axios.get(`/servers/GetApplianceList`);
		return data;
	}

	public async getIsFeaturesConfigControlledByFile(): Promise<AxiosResponse<boolean>> {
		return await this.axios.get("/Features/GetFunctionalityExists");
	}

	public async fetchAllTours(): Promise<any> {
		return await this.axios.get("/Tours/GetAllTours");
	}

	public async fetchDeviceList(groupID: number): Promise<any> {
		return await this.axios.get(`/Tours/GetAllDevices/${groupID}`);
	}

	public async searchDeviceList(groupID: number, searchText: string): Promise<any> {
		return await this.axios.get(`/Tours/SearchDeviceList/${groupID}/${searchText}`);
	}

	public async addTour(tour: any): Promise<any> {
		return await this.axiosJson.post("/Tours/InsertTour", tour);
	}

	public async updateTour(tour: any): Promise<any> {
		return await this.axiosJson.post("/Tours/UpdateTour", tour);
	}

	public async deleteTour(tourId: number): Promise<any> {
		return await this.axios.delete(`/Tours/DeleteTour/${tourId}`);
	}

	public async fetchGuardTours(groupID: number): Promise<any> {
		return await this.axios.get(`/GuardTour/GuardToursByGroup/${groupID}`);
	}

	public async fetchGuardTour(guardTourId: number): Promise<any> {
		return await this.axios.get(`/GuardTour/GuardTour/${guardTourId}`);
	}

	public async fetchAssessmentTours(groupId: number): Promise<any> {
		return await this.axios.get(`/GuardTour/AssessmentTours/${groupId}`);
	}

	public async addGuardTour(guardTour: any): Promise<any> {
		return await this.axiosJson.post("/GuardTours/CreateGuardTour", guardTour);
	}

	public async deleteGuardTour(guardTourId: number): Promise<any> {
		return await this.axios.delete(`/GuardTours/DeleteGuardTour/${guardTourId}`);
	}

	public async updateGuardTour(tour: any): Promise<any> {
		return await this.axiosJson.post("/GuardTours/UpdateGuardTour", tour);
	}

	public async getTenants(): Promise<any> {
		return await this.axios.get("/Groups/Tenants");
	}

	public async getWeeklyDisarmSchedule(groupID: number, regionUrl?: string): Promise<any> {
		return await this.axios.get(`/Groups/WeeklyDisarmSchedule/${groupID}`, { regionUrl });
	}

	public async fetchResponses(
		page: number,
		pageSize: number,
		filter: string,
		serverId: number,
		cancelToken?: CancelToken
	): Promise<any> {
		return await this.axiosJsonDirectInstance.get(`/Responses/GetResponses/${page}/${pageSize}/${serverId}/${filter}`, {
			cancelToken
		});
	}

	public async fetchResponse(responseId: number): Promise<AxiosResponse<Response>> {
		return await this.axiosJson.get(`/Responses/${responseId}`);
	}

	public async createNewResponse(response: Response): Promise<any> {
		return await this.axios.post("/Responses/CreateResponse", response);
	}

	public async updateResponse(response: Response): Promise<any> {
		return await this.axiosJson.put("/Responses/UpdateResponse", response);
	}

	public async deleteResponse(responseId: number): Promise<any> {
		return await this.axiosJson.delete(`/Responses/DeleteResponse/${responseId}`);
	}

	public async triggerTestResponse(responseId: number): Promise<AxiosResponse<ResponseTestResult>> {
		return await this.axiosJson.post(`/Responses/TriggerTest/${responseId}`);
	}

	public async fetchCachedResponsesByServerType(
		serverTypeId: number,
		maximumResponses: number = 0,
		regionUrl: string = undefined
	): Promise<AxiosResponse<Response[]>> {
		return this.axiosJson.get(`/Responses/ByServerType/${serverTypeId}/${maximumResponses}`, { regionUrl });
	}

	public async searchCachedResponses(
		searchText: string,
		serverTypeId: number,
		regionUrl: string = undefined
	): Promise<AxiosResponse<Response[]>> {
		return this.axiosJson.get(`/Responses/SearchCache/${searchText}/${serverTypeId}`, { regionUrl });
	}

	public async getDefaultResponseGrouping(cancelToken?: CancelToken): Promise<any> {
		return await this.axios.get("/Responses/GetDefaultResponseGrouping", {
			cancelToken
		});
	}

	public async getSupportedAlarmActionTypes(): Promise<AxiosResponse<AlarmActionType>> {
		return await this.axiosJson.get("/Alarms/SupportedActionTypes");
	}

	public async getAlarmActions(alarmId: number): Promise<AxiosResponse<AlarmAction[]>> {
		return await this.axiosJson.get(`/Alarms/${alarmId}/Actions`);
	}

	public async createAlarmAction(alarmAction: AlarmAction): Promise<AxiosResponse<AlarmAction>> {
		return await this.axiosJson.post(`/Alarms/${alarmAction.responseID}/Actions`, alarmAction);
	}

	public async updateAlarmAction(alarmAction: AlarmAction): Promise<AxiosResponse<AlarmAction>> {
		return await this.axiosJson.put(`/Alarms/${alarmAction.responseID}/Actions`, alarmAction);
	}

	public async deleteAlarmAction(alarmAction: AlarmAction): Promise<AxiosResponse<boolean>> {
		return await this.axiosJson.delete(`/Alarms/${alarmAction.responseID}/Actions/${alarmAction.responseActionID}`);
	}

	public async getLicenseDetails(tenantGroupId?: number): Promise<any> {
		if (tenantGroupId) {
			const { data } = await this.axios.get(`/License/Details/?tenantGroupId=${tenantGroupId}`);
			return data;
		} else {
			const { data } = await this.axios.get(`/License/Details/`);
			return data;
		}
	}

	public async getUserLimitDefinitions(): Promise<Domain[]> {
		return (await this.axios.get("/License/LicensedUserPermissions")).data;
	}

	public async setLicenseWithFile(licenseFile: any, tenantGroupId?: number): Promise<any> {
		// Append the file to the request via FormData
		const files = new FormData();
		files.append("file", licenseFile, licenseFile.name);
		return await axiosNoContentTypeInstance.post(`/License/Install/?tenantGroupId=${tenantGroupId}`, files);
	}

	public async getTotalCameraUsage(tenantGroupId?: number): Promise<any> {
		const { data } = await this.axios.get(`/LicenseUsage/TotalCameras/?tenantGroupId=${tenantGroupId}`);
		return data;
	}

	public async getTotalOperatorUsage(tenantGroupId?: number): Promise<any> {
		const { data } = await this.axios.get(`/LicenseUsage/TotalOperators/?tenantGroupId=${tenantGroupId}`);
		return data;
	}

	public async getTotalGuardUsage(tenantGroupId?: number): Promise<any> {
		const { data } = await this.axios.get(`/LicenseUsage/TotalGuards/?tenantGroupId=${tenantGroupId}`);
		return data;
	}

	public async fetchAllSyncSystems(currentPage: number, pageSize: number): Promise<any> {
		return await this.axiosJson.get(`/Sync/SyncSystems/${currentPage}/${pageSize}`);
	}

	public async getSyncSystem(syncSystemId: number): Promise<any> {
		return await this.axiosJson.get(`/Sync/SyncSystem/${syncSystemId}`);
	}

	public async querySyncSystems(currentPage: number, pageSize: number, filter: string): Promise<any> {
		return await this.axiosJson.get(`/Sync/QuerySyncSystems/${currentPage}/${pageSize}/${filter}`);
	}

	public async querySyncSystemsByTitle(currentPage: number, pageSize: number, filter: string): Promise<any> {
		return await this.axiosJson.get(`/Sync/QuerySyncSystemsByTitle/${currentPage}/${pageSize}/${filter}`);
	}

	public async fetchServerTypesWithCanSync(): Promise<any> {
		return await this.axiosJson.get(`/Sync/ServerTypesCanSync`);
	}

	public async saveNewSyncSystem(newSyncSystem: any): Promise<any> {
		return await this.axiosJson.post("/Sync/CreateNewSyncSystem", newSyncSystem);
	}

	public async updateSyncSystem(updateSyncSystem: any): Promise<any> {
		return await this.axiosJson.put("/Sync/UpdateSyncSystem", updateSyncSystem);
	}

	public async deleteSyncSystem(syncSystemId: number): Promise<any> {
		return await this.axiosJson.delete(`/Sync/DeleteSyncSystem/${syncSystemId}`);
	}

	public async fetchSyncEntries(currentPage: number, pageSize: number): Promise<any> {
		return await this.axiosJson.get(`/Sync/GroupSyncs/${currentPage}/${pageSize}`);
	}

	public async saveNewSyncEntry(newSyncSystem: any): Promise<any> {
		return await this.axiosJson.post("/Sync/CreateNewSyncEntry", newSyncSystem);
	}

	public async updateSyncEntry(updatedSyncEntry: any): Promise<any> {
		return await this.axiosJson.put("/Sync/UpdateGroupSync", updatedSyncEntry);
	}

	public async deleteSyncEntry(groupSyncId: number): Promise<any> {
		return await this.axiosJson.delete(`/Sync/DeleteGroupSync/${groupSyncId}`);
	}

	public async resyncSyncEntry(groupSyncId: number): Promise<any> {
		return await this.axiosJson.post(`/Sync/ForceGroupResync/${groupSyncId}`);
	}

	public async verifySyncEntries(syncEntries: any): Promise<any> {
		return await this.axiosJson.post(`/Sync/verifySyncEntriesStatus`, syncEntries);
	}

	public async fetchSyncTypes(): Promise<any> {
		return await this.axiosJson.get("/sync/GetSyncTypes");
	}

	public async querySyncEntries(currentPage: number, pageSize: number, filter: string): Promise<any> {
		return await this.axiosJson.get(`/Sync/QuerySyncEntries/${currentPage}/${pageSize}/${filter}`);
	}

	public async getExternalMapLayerTypes(): Promise<any> {
		return await this.axios.get("/ExternalMapLayers/GetTypes");
	}

	public async getExternalMapLayers(): Promise<any> {
		return await this.axios.get("/ExternalMapLayers");
	}

	public async createOrUpdateExternalMapLayer(externalMapLayer: ExternalMapLayer): Promise<any> {
		return await this.axiosJson.put("/ExternalMapLayers", externalMapLayer);
	}

	public async deleteExternalMapLayer(externalMapLayerID: number): Promise<any> {
		return await this.axiosJson.delete(`/ExternalMapLayers/${externalMapLayerID}`);
	}

	public async updateExternalMapLayerUserLink(externalMapLayerUserLink: any) {
		return await this.axiosJson.put("/ExternalMapLayerUserLink", externalMapLayerUserLink);
	}

	public async loadAllPrefTypes(): Promise<any> {
		return await this.axiosJson.get("/settings/GetAllPrefTypes");
	}

	public async loadAllIcons(): Promise<any> {
		return await this.axiosJson.get("/settings/GetAllIcons");
	}

	public async loadAllPrefs(): Promise<any> {
		return await this.axiosJson.get("/settings/GetAllPrefs");
	}

	public async updatePref(pref: Pref): Promise<any> {
		return await this.axiosJson.put("/settings/UpdatePref", pref);
	}

	public async createPref(pref: Pref): Promise<any> {
		return await this.axiosJson.post("/settings/CreatePref", pref);
	}

	public async getDevicesByMapLayerItemType(
		groupId: number,
		deviceTypeId: number,
		filter: string | null | undefined
	): Promise<any> {
		let uri = `/Devices/GetDevicesByMapLayerItemType/${groupId}/${deviceTypeId}`;
		if (filter && filter.length > 0) {
			uri += `?filter=${filter}`;
		}
		const { data } = await this.axiosJson.get(uri);
		return data;
	}

	public async fetchEventOutcomesForAnyAlarm(): Promise<EventOutcome[]> {
		const response = await this.axios.get("/Outcomes/ForAnyAlarm");
		return response.data;
	}

	public async fetchEventOutcomesForUser() {
		return await this.axios.get("/Outcomes/GetEventOutcomes");
	}

	public async fetchEventOutcomeTree() {
		return await this.axios.get("/Outcomes/GetEventOutcomeTree");
	}

	public async updateEventOutcome(eventOutcome: EventCategory) {
		return await this.axiosJsonDirectInstance.post("/Outcomes/UpdateOutcome", eventOutcome);
	}

	public async deleteEventOutcome(eventOutcome: EventCategory) {
		return await this.axiosJson.post("/Outcomes/DeleteOutcome", eventOutcome);
	}

	public async createEventOutcome(eventOutcome: EventCategory) {
		return await this.axiosJson.post("/Outcomes/CreateOutcome", eventOutcome);
	}

	// Case management
	public async getTemplates(eventId: number) {
		return await this.axios.get(`/cases/templates/${eventId}`);
	}

	public async getTemplatesForUser() {
		return await this.axios.get("/cases/templates");
	}

	public async raiseToCases(caseData: CaseData) {
		return await this.axiosJsonDirectInstance.post("/cases/raiseToCases", caseData);
	}

	public async loadReportDatePref() {
		return (await this.axiosJson.get("/Reports/ReportDatePref")).data;
	}

	public async casesLogin(): Promise<{ url: string; token: string }> {
		return (await this.axiosJson.post("/cases/casesLogin")).data;
	}

	public async updateEventRecordLocation(eventRecordId: number, latLng: any, elevation: number): Promise<any> {
		return await this.axiosJson.put(`EventRecords/UpdateEventRecordLocation/${eventRecordId}/${elevation}`, {
			lat: latLng.lat,
			lng: latLng.lng
		});
	}

	public async eventQueueRequest(requestData: any, eventQueueRequestUrl: string = "/eventqueue") {
		return await this.axiosJson.post(eventQueueRequestUrl, requestData, {
			baseURL: api.getOriginalApiUrl /*,
			timeout: 15000*/
		});
	}

	public async getIsLastOperator(userId: number): Promise<any> {
		return await this.axiosJson.get(`users/GetIsLastOperator/${userId}`);
	}
	public async cCureResetNonEventAction(data: ManualResetRequest, regionUrl?: string): Promise<any> {
		return this.axios.post("/CCure/ResetNonEventAction", data, { regionUrl });
	}

	public async loadSystemEventRecords(eventIds: number[]): Promise<any> {
		return await this.axiosJson.post("/SystemEvents/EventRecords", eventIds);
	}

	public async ackSystemEvent(eventRecords: number[], eventIds: number[], closeEvent: boolean): Promise<any> {
		return await this.axiosJson.post("/SystemEvents/AckSystemEvent", { eventRecords, eventIds, closeEvent });
	}

	public async getRegions(): Promise<Region[]> {
		// Ensure we hit the local DS for our region list
		const { data } = await this.axiosJson.get("/Region", {
			baseURL: axiosInstance.prototype._originalBaseUrl
		});

		return data;
	}

	public async loadEventSearchDateTimePrefs(): Promise<AxiosResponse<EventSearchPrefs>> {
		return await this.axiosJson.get("/EventReports/DateTimePrefs");
	}

	public async getLoginPreferences(): Promise<LoginPreferences> {
		const { data } = await this.axiosJsonDirectInstance.get("/Login/Preferences");
		return data;
	}

	public async getAvailableDomains(): Promise<Domain[]> {
		const { data } = await this.axiosJsonDirectInstance.get("/Login/Domains");
		return data;
	}

	/**
	 * @summary fetches a list of audio devices by group.
	 */
	public async fetchAudioDevicesByIds(deviceIds: number[]): Promise<AxiosResponse<AudioDevice[]>> {
		return await this.axiosJson.get(`Event/AudioDevicesNearby`, {
			params: {
				deviceIds: deviceIds
			},
			paramsSerializer: params => {
				return qs.stringify(params);
			}
		});
	}

	/**
	 * @summary endpoint to which will set the curent user to the picked up caller
	 */
	public async setCaller(userPickup: PickupCall) {
		return await this.axiosJson.post("/Calls/PickupCall", userPickup);
	}

	public async cancelCall(callCancel: PickupCall) {
		return await this.axiosJson.post("/Calls/CancelCall", callCancel);
	}

	public async closeCall(callClose: PickupCall) {
		return await this.axiosJson.post("/Calls/CloseCall", callClose);
	}

	public async GetCallWaitingPrefs(): Promise<AxiosResponse<CallWaitingPref[]>> {
		return await this.axiosJson.get("/Calls/GetCallWaitingPrefs");
	}

	/**
	 * @summary Get all menu links the user has access to
	 */
	public async getAllExternalMenuLinks(): Promise<AxiosResponse<ExternalMenuLink[]>> {
		return await this.axiosJson.get("ExternalMenuLink");
	}

	/**
	 * @summary Get a menu link by menu link id
	 */
	public async getExternalMenuLinkById(externalMenuLinkId: number): Promise<AxiosResponse<ExternalMenuLink>> {
		return await this.axiosJson.get(`ExternalMenuLink/${externalMenuLinkId}`);
	}

	/**
	 * @summary Delete a menu link by menu link id
	 */
	public async deleteExternalMenuLink(externalMenuLinkId: number): Promise<AxiosResponse<boolean>> {
		return await this.axiosJson.delete(`/ExternalMenuLink/Delete/${externalMenuLinkId}`);
	}

	/**
	 * @summary Update a ExternalMenuLink
	 */
	public async updateExternalMenuLink(externalMapLink: ExternalMenuLink): Promise<AxiosResponse<ExternalMenuLink>> {
		return await this.axiosJson.put(`/UpdateExternalMenuLink`, externalMapLink, {
			headers: {
				"Content-Type": "multipart/form-data"
			}
		});
	}

	/**
	 * @summary Create a ExternalMenuLink
	 */
	public async createExternalMenuLink(externalMapLink: ExternalMenuLink): Promise<AxiosResponse<ExternalMenuLink>> {
		return await this.axiosNoContentTypeInstance.post(`/ExternalMenuLink`, externalMapLink, {
			headers: {
				"Content-Type": "multipart/form-data"
			}
		});
	}

	/**
	 * @summary Get all ExternalMenuLink titles
	 */
	public async GetExternalMenuLinkTitles(): Promise<any> {
		return await this.axios.get("/ExternalMenuLink/GetExternalMenuLinkTitles");
	}

	/**
	 * @summary Claims an event for the logged in user
	 */
	public async ClaimEvent(eventId: number): Promise<AxiosResponse<void>> {
		return await this.axios.get(`/eventqueue/ClaimEvent/${eventId}`);
	}

	/**
	 * @summary Releases a claim on an event for the logged in user
	 */
	public async ReleaseEvent(eventId: number): Promise<AxiosResponse<void>> {
		return await this.axios.get(`/eventqueue/ReleaseEvent/${eventId}`);
	}

	/**
	 * @summary axios endpoint which uploads the csv file to parse it to json
	 * @param rawFile csv file for bulk import
	 * @param onProgressUpdate onprogress handler
	 * @returns the return type for the axios data is any[] | any as we don't know at this point what the strucure of our data will look like.
	 */
	 public async bulkImportParse<T>(
		rawFile: File,
		onProgressUpdate: (newProgress: number) => void,
		bulkType: BulkImportType
	): Promise<AxiosResponse<T>> {

		const encodedFile = await identifyAndEncode(rawFile);

		const formData = new FormData();

		formData.append("file", encodedFile , encodedFile.name);

		const requestConfig = {
			onUploadProgress: (progressEvent: ProgressEvent) => {
				// When axios sends us an upload progress event, invoke our callback with the % figure
				let percentComplete = Math.round((progressEvent.loaded * 100) / progressEvent.total);
				onProgressUpdate(percentComplete);
			}
		};

		return await this.axiosJson.post(`/BulkImport/${bulkType}`, formData, requestConfig);
	}

	public async saveBulkImport(bulkType: BulkImportType, bulkData: any[]): Promise<AxiosResponse<BulkImportResponse>> {
		return await this.axiosJson.post(`/BulkImport/Save/${bulkType}`, { bulkData: JSON.stringify(bulkData) });
	}

	public async getDevicesByTitle(deviceTitles: string[]): Promise<AxiosResponse<DeviceDetails[]>> {
		return await this.axiosJson.post("/Devices/GetDevicesByTitle", deviceTitles);
	}

	public async getResponsesByTitle(responseTitles: string[]): Promise<AxiosResponse<Response[]>> {
		return await this.axiosJson.post("Responses/ResponsesByTitle", responseTitles);
	}

	public async getPrefsFromList(prefRequestList: PrefTypeRequest[]): Promise<Pref[]> {
		return (await this.axiosJson.post(`/settings/GetPrefsFromList`, prefRequestList)).data;
	}

	public async getRecoveryQuestions(): Promise<AxiosResponse<RecoveryQuestion[]>> {
		return await (await this.axiosJson.get("/users/GetRecoveryQuestions")).data;
	}

	public async getUserQuestions(username: string): Promise<AxiosResponse<RecoveryQuestion[]>> {
		return (await this.axiosJson.get(`/users/GetUserQuestions/${username}`)).data;
	}

	public async resetPassword(recoveryDetails: ResetPasswordDetails): Promise<AxiosResponse<ResetPasswordResponse>> {
		return await this.axios.post("/users/ResetPassword", recoveryDetails);
	}

	public async setUserQuestions(updateQuestions: UserQuestionsUpdate): Promise<AxiosResponse<number>> {
		return await this.axios.put(`/users/SetUserQuestions`, updateQuestions);
	}

	public async getUlTypes(): Promise<UlType> {
		const { data } = await this.axios.get("/groups/GetUlTypes");
		return data;
	}

	public async renewPassword(recoveryDetails: RenewPasswordDetails): Promise<AxiosResponse<PasswordChangeResult>> {
		return await this.axios.post("/users/RenewExpiredPassword", recoveryDetails);
	}

	/**
	 * @summary Get a list of landing page options avaliable to the user
	 * @returns AxiosPromiseResponse List of landing pages
	 */
	public async getLandingPages(): Promise<AxiosResponse<string[]>> {
		return await this.axiosJson.get("/users/LandingPages");
	}

	/**
	 * @summary Update users desired landing page
	 * @returns AxiosPromiseResponse N/A
	 */
	public async setLandingPage(landingPage: string): Promise<AxiosResponse<null>> {
		return this.axiosJson.put(`/users/LandingPage`, { landingPage });
	}

	/**
	 * @summary Get the user's landing page
	 * @returns AxiosPromiseResponse user's landing page
	 */
	public async getLandingPageForUser(): Promise<AxiosResponse<string>> {
		return this.axiosJson.get("/users/LandingPage");
	}

	public async getServerTypeSetupPrefs(): Promise<AxiosResponse<ServerTypeSetupPrefs>> {
		return await this.axiosJson.get("/servertypeevents/GetServerTypeSetupPrefs");
	}

	public async getAllRegions(): Promise<AxiosResponse<Region[]>> {
		return (await this.axios.get("/RegionsSetup")).data;
	}

	public async createRegion(region: Region): Promise<AxiosResponse<Region>> {
		return (await this.axios.post("/RegionsSetup", region)).data;
	}

	public async updateRegion(region: Region): Promise<AxiosResponse<Region>> {
		return (await this.axios.put("/RegionsSetup", region)).data;
	}

	public async deleteRegion(regionId: number): Promise<AxiosResponse<number>> {
		return (await this.axios.delete(`/RegionsSetup?regionId=${regionId}`)).data;
	}

	public async validateSitePassword(payload: ValidateSitePasswordRequest): Promise<boolean> {
		const { data } = await this.axiosJson.post(`/users/ValidateSitePassword`, payload);
		return data;
	}

	public async getCameraPresets(device: DeviceDetails) : Promise<DeviceConfigurationPreset[]> {
		const { data } = await this.axiosJson.get(`/Presets/GetPresets`, {
			params: {
				deviceId: device.deviceID,
				groupId: device.groupID
			},
			paramsSerializer: params => {
				return qs.stringify(params);
			}
		});
		return data;
	}

	public async createNewCameraPreset(presetConfigPayload : DeviceConfigurationPresetPayload) : Promise<boolean> {
		const { data } = await this.axiosJson.post(`/Presets/CreatePreset`, presetConfigPayload);
		return data;
	}

	public async updateCameraPreset(presetConfigPayload : DeviceConfigurationPresetPayload) : Promise<boolean> {
		const { data } = await this.axiosJson.put(`/Presets/UpdatePreset`, presetConfigPayload);
		return data;
	}

	public async deleteCameraPreset(presetConfigPayload : DeviceConfigurationPresetPayload) : Promise<boolean> {
		const { data } = await this.axiosJson.delete(`/Presets/DeletePreset`, {
			params: {
				deviceId: presetConfigPayload.deviceId,
				groupId: presetConfigPayload.groupId,
				name: presetConfigPayload.name,
				position: presetConfigPayload.position
			},
			paramsSerializer: params => {
				return qs.stringify(params);
			}
		});
		return data;
	}
}

const api = new ImmixCloudV2ApiHttp();
export default api;
